
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Activity } from "../ActivityTypes";
import { useActivityNavigation } from "../activityUtils";
import { publishA2AMessage } from "../utils/a2aActivityMessaging";

export const useActivityMutations = (targetTab: "today" | "tomorrow", tomorrowActivities: Activity[] | null, setTomorrowActivities: React.Dispatch<React.SetStateAction<Activity[] | null>>) => {
  const queryClient = useQueryClient();
  const handleActivityNavigation = useActivityNavigation();

  const startActivityMutation = useMutation({
    mutationFn: async (activity: Activity) => {
      publishA2AMessage(
        'activity-started',
        `Starting activity: ${activity.title}`,
        { activityId: activity.id, activityType: activity.type },
        activity.priority === 'high' ? 'high' : 'normal'
      );
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API call
      return { ...activity, status: "in_progress" } as Activity;
    },
    onSuccess: (updatedActivity) => {
      queryClient.setQueryData(["suggested-activities"], (oldData: Activity[] = []) =>
        oldData.map((act) => (act.id === updatedActivity.id ? updatedActivity : act))
      );
      if (targetTab === "tomorrow" && tomorrowActivities) {
        setTomorrowActivities(prev => prev!.map(act => act.id === updatedActivity.id ? updatedActivity : act));
      }
      toast.success(`Started: ${updatedActivity.title}`);
      handleActivityNavigation(updatedActivity);
    },
    onError: (err, variables) => {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error("Error starting activity:", variables.title, err);
      toast.error(`Failed to start: ${variables.title}. Please try again.`);
      publishA2AMessage(
        'activity-error',
        `Error starting activity '${variables.title}': ${errorMessage}`,
        { activityId: variables.id, error: errorMessage },
        'high'
      );
    },
  });

  const completeActivityMutation = useMutation({
    mutationFn: async (activity: Activity) => {
      publishA2AMessage(
        'activity-completed',
        `Completing activity: ${activity.title}`,
        { activityId: activity.id, activityType: activity.type }
      );
      await new Promise((resolve) => setTimeout(resolve, 400)); // Simulate API call
      return { ...activity, status: "completed" } as Activity;
    },
    onSuccess: (completedActivity) => {
      queryClient.setQueryData(["suggested-activities"], (oldData: Activity[] = []) =>
        oldData.map(act => act.id === completedActivity.id ? completedActivity : act)
      );
      if (targetTab === "tomorrow" && tomorrowActivities) {
        setTomorrowActivities(prev => prev!.map(a => a.id === completedActivity.id ? completedActivity : a));
      }
      toast.success(`Marked completed: ${completedActivity.title}`);
    },
    onError: (err, variables) => {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error("Error completing activity:", variables.title, err);
      toast.error(`Error marking '${variables.title}' complete.`);
      publishA2AMessage(
        'activity-error',
        `Error completing activity '${variables.title}': ${errorMessage}`,
        { activityId: variables.id, error: errorMessage },
        'high'
      );
    },
  });

  return {
    startActivityMutation,
    completeActivityMutation,
  };
};
